import React from 'react';
import { Shield, Car, Clock, MessageCircle, Mail, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { contactInfo } from '../mock/fareData';

/*
 * 2026-06-04 — Dedicated /premier landing page.
 * Mirrors the already-approved homepage Premier Coming Soon section
 * (same monogram, same eyebrow, same headline, same body copy, same 3 pillars).
 * Adds two existing-pattern contact CTAs (WhatsApp + Email) — no new service
 * promises, no pricing, no "VIP" wording.
 */
const Premier = () => {
  return (
    <>
      <SEOHead
        title="Taxi-98 Premier Coming Soon | Nassau, The Bahamas"
        description="Taxi-98 Premier is our upcoming elevated private transportation service for clients who want more comfort, privacy, and a higher level of service in Nassau."
        canonical="/premier"
      />

      <main
        data-testid="premier-page"
        className="relative min-h-screen bg-gradient-to-br from-black via-gray-950 to-black overflow-hidden pt-28 pb-20 sm:pt-32 sm:pb-28"
      >
        {/* Subtle gold-tinted accents */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500/8 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-amber-400/8 rounded-full blur-3xl pointer-events-none"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            {/* Brand monogram — identical asset to homepage Premier section */}
            <div className="flex justify-center mb-8">
              <img
                src="/images/brand/premier-monogram.png"
                alt="Taxi-98 Premier — Nassau, The Bahamas"
                className="w-48 sm:w-56 lg:w-64 h-auto"
                loading="eager"
                data-testid="premier-page-monogram"
              />
            </div>

            {/* Eyebrow */}
            <span
              className="inline-block bg-amber-400/10 border border-amber-300/30 text-amber-200 text-xs font-bold tracking-[0.22em] uppercase px-4 py-1.5 rounded-full mb-6"
              data-testid="premier-page-eyebrow"
            >
              Coming Soon
            </span>

            <h1
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6"
              data-testid="premier-page-headline"
            >
              Taxi-98 Premier Coming Soon
            </h1>

            <p className="text-lg sm:text-xl text-gray-300 leading-relaxed mb-6 max-w-3xl mx-auto">
              Taxi-98 Premier is our upcoming elevated private transportation service for clients who want more comfort, privacy, and a higher level of service in Nassau. Designed for airport transfers, private rides, special occasions, business travel, and premium guest experiences.
            </p>

            <p className="text-base text-gray-400 italic mb-10">
              More details coming soon.
            </p>

            {/* Premium pillars — identical to homepage section */}
            <div
              className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 max-w-3xl mx-auto mb-12"
              data-testid="premier-page-pillars"
            >
              <div className="bg-white/5 backdrop-blur-sm border border-amber-300/15 rounded-xl py-5 px-4">
                <Shield className="w-6 h-6 text-amber-300 mx-auto mb-2" />
                <p className="text-sm font-semibold text-white">Private &amp; Discreet</p>
              </div>
              <div className="bg-white/5 backdrop-blur-sm border border-amber-300/15 rounded-xl py-5 px-4">
                <Car className="w-6 h-6 text-amber-300 mx-auto mb-2" />
                <p className="text-sm font-semibold text-white">Elevated Comfort</p>
              </div>
              <div className="bg-white/5 backdrop-blur-sm border border-amber-300/15 rounded-xl py-5 px-4">
                <Clock className="w-6 h-6 text-amber-300 mx-auto mb-2" />
                <p className="text-sm font-semibold text-white">By Appointment</p>
              </div>
            </div>

            {/* Contact / Request CTA — uses existing site contact patterns only */}
            <div className="border-t border-amber-300/15 pt-10">
              <p className="text-sm text-amber-200/80 uppercase tracking-[0.2em] font-semibold mb-5">
                Register Your Interest
              </p>
              <p className="text-base sm:text-lg text-gray-300 max-w-2xl mx-auto mb-7">
                Get in touch to be notified when Taxi-98 Premier launches, or to inquire about a private booking ahead of the public release.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
                <a
                  href={`https://wa.me/${contactInfo.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(
                    'Hello Taxi-98, I would like to register my interest in Taxi-98 Premier.'
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="premier-page-whatsapp-cta"
                  className="inline-flex items-center justify-center gap-2 bg-amber-400/15 border border-amber-300/40 hover:bg-amber-400/25 text-amber-100 hover:text-white px-6 py-3 rounded-lg font-semibold text-base transition-all duration-300"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span>Contact on WhatsApp</span>
                </a>
                <a
                  href={`mailto:${contactInfo.emailStephen}?subject=${encodeURIComponent(
                    'Taxi-98 Premier — Register My Interest'
                  )}`}
                  data-testid="premier-page-email-cta"
                  className="inline-flex items-center justify-center gap-2 bg-white/5 border border-white/15 hover:bg-white/10 text-gray-100 px-6 py-3 rounded-lg font-semibold text-base transition-all duration-300"
                >
                  <Mail className="w-4 h-4" />
                  <span>Email Us</span>
                </a>
              </div>
            </div>

            {/* Back to home */}
            <div className="mt-12">
              <Link
                to="/"
                data-testid="premier-page-back-home"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-amber-200 text-sm font-medium transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back to Home</span>
              </Link>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default Premier;
